import { useQuery } from '@tanstack/react-query';
import { intelligenceRecords } from '@cwos/api-client';
import { Pill, TableCard, type Column } from '@cwos/ui-admin';
import { useNav } from '../../navigation.js';
import { listPhase } from '../../listState.js';
import { ListState } from '../../ListStates.js';
import { LearningSignals } from './LearningSignals.js';
import { withheldLine, windowLine } from './recordsLogic.js';

/**
 * INTELLIGENCE → RECOMMENDATIONS. What Phibian advised, and what happened.
 *
 * ===========================================================================
 * WHY THIS SCREEN EXISTS
 * ===========================================================================
 * Founder ruling 2 of 2026-09-08: "A seeded role must not hold customer
 * permissions for capabilities it cannot actually reach", and for each of the
 * Analyst persona's unreachable keys, "surface it through the proper six-hub
 * customer UI and prove reachability, or remove it".
 *
 * `recommendation:read`, `outcome:read` and `learning:read` are seeded to
 * org_admin, supervisor and analyst. All three routes existed and answered;
 * none of them had a client at all — no api-client module, no screen, no route
 * entry. This is the surface, and it is the answer for those three keys rather
 * than removing them, because they are read-only analysis surfaces, which is
 * the one category the ruling names as legitimately an Analyst's.
 *
 * ===========================================================================
 * WHAT THIS SCREEN MUST NEVER DO
 * ===========================================================================
 * The capability ledger's own action line for this surface says it in capitals:
 * render `not_yet_measured` as its own state and never as a failed outcome, and
 * show `unavailable` rather than an empty measurement. Both are done in
 * `recordsLogic.ts` so they are unit-testable, and the detail screen renders
 * the sentence rather than a number.
 *
 * `withheld` and `scanned` are on the wire and are rendered OUTSIDE the list
 * state, so they still appear when the list is empty — which is the only case
 * where the difference between "there is little advice" and "there is advice
 * you may not see" actually matters.
 *
 * ===========================================================================
 * THE GATE
 * ===========================================================================
 * The route charges `RECOMMENDATION_READ_KEY`, which is the literal the server
 * guard names (`recommendation-record/routes.ts`). `outcome:read` is NOT a door
 * key — the outcome admission gate checks it per record inside the detail read,
 * and a caller without it gets `outcome.state = 'withheld'`, not a 403. So it
 * would be wrong to gate this screen on it, and wrong to render its absence as
 * an empty measurement.
 */

/** The literal `recommendation-record/routes.ts` guards both reads with. */
export const RECOMMENDATION_READ_KEY = 'recommendation:read';

/**
 * What this surface is, for the no-permission screen.
 *
 * It names `outcome:read` as well, because the person most likely to read this
 * is an administrator deciding what to grant, and "you can see the advice but
 * not whether it worked" is a real and separate configuration that is not
 * discoverable anywhere else.
 */
export const RECOMMENDATION_RECORDS_IS =
  'Recommendations is the record of advice Phibian gave your organization: the question asked, the headline, the doctrine it was composed under, the retrievals it rested on, how much those retrievals actually support, and what happened afterwards. `recommendation:read` opens it. Which records you see is decided per record by the evidence underneath them — advice grounded in a retrieval you could not have run is withheld from you and counted, never silently dropped. `outcome:read` is what lets the page say whether an outcome was recorded, rather than leaving “not yet measured” indistinguishable from “we may not tell you”.';

export function RecommendationRecords() {
  const navigate = useNav();
  const list = useQuery({
    queryKey: ['intelligence', 'recommendations'],
    queryFn: () => intelligenceRecords.listRecommendations(),
  });

  const rows = list.data?.recommendations ?? [];
  const withheld = withheldLine(list.data?.withheld, 'recommendation');
  const window = list.data ? windowLine(list.data.scanned, list.data.windowExhausted) : null;

  const columns: Column<intelligenceRecords.Recommendation>[] = [
    {
      key: 'what',
      header: 'What Phibian advised',
      render: (row) => (
        <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
          <span className="rn-strong">{row.headline}</span>
          <span className="rn-muted">{row.question}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'About',
      render: (row) => <span className="rn-muted">{row.subject}</span>,
    },
    {
      key: 'standing',
      header: 'Standing',
      render: (row) => (
        // `no_feasible_action` is a real and deliberate answer — "there was
        // nothing to advise" — and it is shown as one rather than as a record
        // with a missing recommendation.
        <Pill variant={row.no_feasible_action ? 'unsure' : 'plain'}>
          {row.no_feasible_action
            ? 'No feasible action'
            : row.advisory_only
              ? 'Advisory'
              : row.confirmation_required
                ? 'Needs confirmation'
                : 'Actionable'}
        </Pill>
      ),
    },
    {
      key: 'when',
      header: 'Recorded',
      render: (row) => <span className="rn-muted">{row.created_at.slice(0, 10)}</span>,
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 1000 }}>
      <header className="rn-col">
        <h1>Recommendations</h1>
        <p className="rn-muted">
          Every piece of advice Phibian recorded for your organization, with the evidence it rested
          on and what happened afterwards. Which of them you see depends on the retrievals
          underneath each one, not on this page.
        </p>
      </header>

      {/* OUTSIDE the list state on purpose: a withholding matters most when the
          list is empty, and a sentence rendered inside `empty` would be the one
          sentence that never appears when it is needed. */}
      {withheld ? <p className="rn-muted">{withheld}</p> : null}
      {window ? <p className="rn-muted">{window}</p> : null}

      <ListState
        phase={listPhase(list, rows.length)}
        surface="table"
        rows={5}
        failedMessage="We could not load the recommendation record. Nothing has changed."
        onRetry={() => void list.refetch()}
        empty={
          <p className="rn-muted">
            No advice has been recorded for your organization yet. This is a measured zero: the
            record was read and it was empty.
          </p>
        }
      >
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          onRowOpen={(row) => navigate(`/intelligence/recommendations/${row.id}`)}
          caption="Recommendations"
        />
      </ListState>

      <LearningSignals />
    </div>
  );
}
